import useWebData from '../../hooks/useWebData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faHandsHelping, faCar, faThumbsUp, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { getAboutUs } from '../../services/aboutUs';
import DOMPurify from 'dompurify';
import { tenantId } from '../../config/api';
import { Helmet } from 'react-helmet-async';

const AboutUs = () => {
  const { webData }: any = useWebData();
  const [aboutUs, setAboutUs] = useState<string>("");
  const [aboutUsImage, setAboutUsImage] = useState<string>("");
  const [ourMission, setOurMission] = useState<string>("");
  const [ourMissionImage, setOurMissionImage] = useState<string>("");
  const [ourVision, setOurVision] = useState<string>("");
  const [ourVisionImage, setOurVisionImage] = useState<string>("");
  const [fleetSelection, setFleetSelection] = useState<string>("");
  const [roadsideAssistance, setRoadsideAssistance] = useState<string>("");
  const [insurance, setInsurance] = useState<string>("");

  useEffect(() => {
    getAboutUs(tenantId)
      .then(data => {
        if (data?.length > 0) {
          const item = data[0];
          setAboutUs(DOMPurify.sanitize(item.aboutUs));
          setAboutUsImage(item.aboutUsImage);
          setOurMission(DOMPurify.sanitize(item.ourMission));
          setOurMissionImage(item.ourMissionImage);
          setOurVision(DOMPurify.sanitize(item.ourVision));
          setOurVisionImage(item.ourVisionImage);
          setFleetSelection(item.fleetSelection);
          setRoadsideAssistance(item.roadsideAssistance);
          setInsurance(item.insurance);
        }
      })
      .catch(error => console.error(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Reliable Vehicle & Van Rental Services | About</title>
        <meta
          name="description"
          content="Learn about {CompanyName}, a trusted vehicle and van rental service offering clean vehicles, flexible plans, airport transfers, and reliable support."
        />
      </Helmet>
      <div className="w-full overflow-x-hidden mt-[130px]">
        <div
          className="relative md:h-[100vh] bg-center bg-cover flex items-start justify-center pt-24 md:pt-32"
          style={{
            backgroundImage: `url(${aboutUsImage})`,
            backgroundAttachment: 'fixed',
            backgroundPosition: 'top center',
            backgroundSize: 'cover',
          }}
        >
          <div className="absolute inset-0 bg-black/60 z-0"></div>
          <div className="relative z-10 text-center mt-4 px-4 text-white max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold drop-shadow-md mb-4">
              About Us
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed drop-shadow-sm"
              dangerouslySetInnerHTML={{ __html: aboutUs }}
            />
          </div>
        </div>

        <section className="px-4 py-10 md:py-14 max-w-6xl mx-auto">
          <h2 className="text-center text-2xl md:text-4xl font-bold text-gray-800 mb-10">
            We're Committed to Providing Quality Vehicle Rental Services
          </h2>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-center text-center hover:shadow-xl transition duration-300">
              <div className="bg-primary/10 text-primary p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faCar} className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Clean & Well-Maintained Rental Vehicles</h3>
              <p className="text-sm text-gray-600 mt-2">
                Well-maintained and ready for every journey.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-center text-center hover:shadow-xl transition duration-300">
              <div className="bg-primary/10 text-primary p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faExchangeAlt} className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Flexible Vehicle Rental Plans</h3>
              <p className="text-sm text-gray-600 mt-2">
                Rental options tailored to your lifestyle and needs.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-center text-center hover:shadow-xl transition duration-300">
              <div className="bg-primary/10 text-primary p-4 rounded-full mb-4">
                <FontAwesomeIcon icon={faHandsHelping} className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">24/7 Customer Support for Rentals</h3>
              <p className="text-sm text-gray-600 mt-2">
                Friendly, professional assistance every step of the way.
              </p>
            </div>
          </div>
        </section>

        <section className="flex flex-col lg:flex-row items-center py-16 px-4 md:px-20 gap-8 bg-gray-50">
          <div className="lg:w-1/2">
            <img src={ourMissionImage} alt="Our mission to provide reliable vehicle rental services" className="rounded-lg shadow w-full h-[320px] object-cover" />
          </div>
          <div className="lg:w-1/2">
            <h2 className="text-xl md:text-3xl font-bold mb-4">Our Mission in Vehicle Rental Services</h2>
            <p className="text-gray-700 text-sm md:text-lg" dangerouslySetInnerHTML={{ __html: ourMission }} />
          </div>
        </section>

        <section className="flex flex-col-reverse lg:flex-row items-center py-16 px-4 md:px-20 gap-8">
          <div className="lg:w-1/2">
            <h2 className="text-xl md:text-3xl font-bold mb-4">Our Vision for Vehicle Rental Services</h2>
            <p className="text-gray-700 text-sm md:text-lg mb-4" dangerouslySetInnerHTML={{ __html: ourVision }} />
          </div>
          <div className="lg:w-1/2">
            <img src={ourVisionImage} alt="Our Vision" className="rounded-lg shadow w-full h-[320px] object-cover" />
          </div>
        </section>

        <section className="bg-gray-100 py-12 px-6 lg:px-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow hover:shadow-md transition">
            <FontAwesomeIcon icon={faCar} className="text-5xl mb-4" style={{ color: webData?.websiteHeaderColor }} />
            <h3 className="text-lg font-bold mb-2 text-gray-700">Fleet Selection</h3>
            <p className="text-sm text-gray-600">{fleetSelection}</p>
          </div>
          <div className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow hover:shadow-md transition">
            <FontAwesomeIcon icon={faThumbsUp} className="text-5xl mb-4" style={{ color: webData?.websiteHeaderColor }} />
            <h3 className="text-lg font-bold mb-2 text-gray-700">Roadside Assistance</h3>
            <p className="text-sm text-gray-600">{roadsideAssistance}</p>
          </div>
          <div className="flex flex-col items-center text-center p-4 bg-white rounded-lg shadow hover:shadow-md transition">
            <FontAwesomeIcon icon={faUserPlus} className="text-5xl mb-4" style={{ color: webData?.websiteHeaderColor }} />
            <h3 className="text-lg font-bold mb-2 text-gray-700">Insurance</h3>
            <p className="text-sm text-gray-600">{insurance}</p>
          </div>
        </section>
      </div>
    </>

  );
};

export default AboutUs;